/**
 * deweyCodeParser.ts - Dewey Decimal Code Parsing Utilities
 *
 * Handles hierarchical Dewey codes where parent-child relationships are encoded as:
 * - "300.301" (parent 300, child 301)
 * - "900.956.704" (root 900, contains path to 956.704)
 * - "900.956.70401" (deeply nested code)
 *
 * Standard Dewey Format: XXX.XXXXX (3 digits, optional decimal, optional subdivision)
 */

// ============================================================================
// Types & Constants
// ============================================================================

/**
 * Feature flag to control whether to hide categories without resolved labels.
 * When true, categories that couldn't be resolved to a label will be filtered out.
 * When false, unresolved categories will display their code as the label.
 */
export const HIDE_UNRESOLVED_CATEGORIES = true;

export interface DeweyCodeInfo {
    fullCode: string;
    rootCode: string;
    itemCode: string;
    parentCode: string | null;
    level: number;
    isValid: boolean;
}

export interface CodeSearchResult {
    code: string;
    label: string;
    matchType: 'exact' | 'normalized' | 'partial' | 'none';
}

/**
 * Checks if a label was successfully resolved (i.e., label is different from the code).
 * @param code - The original code
 * @param label - The resolved label
 * @returns true if the label was successfully resolved, false if it's just the code
 */
export function isLabelResolved(code: string, label: string): boolean {
    return label !== code && label !== '';
}



// ============================================================================
// Validation
// ============================================================================

/**
 * Validates if a string is a properly formatted Dewey code segment.
 * Accepts: "300", "956", "704", "70401", "956.704"
 */
export function isValidDeweySegment(segment: string): boolean {
    if (!segment || typeof segment !== 'string') return false;
    // Allow digits and single decimal point
    return /^\d+(\.\d+)?$/.test(segment);
}

/**
 * Validates a full hierarchical Dewey code.
 * Accepts: "900.956.704", "300.301", "300"
 */
export function isValidHierarchicalCode(code: string): boolean {
    if (!code || typeof code !== 'string') return false;
    const parts = code.split('.');
    return parts.length > 0 && parts.every(p => /^\d+$/.test(p));
}

/**
 * Sanitizes input by trimming and removing invalid characters.
 */
export function sanitizeCode(code: string): string {
    if (!code || typeof code !== 'string') return '';
    return code.trim().replace(/[^\d.]/g, '');
}

// ============================================================================
// Core Extraction Functions
// ============================================================================

/**
 * Parses a hierarchical Dewey code into its components.
 *
 * @example
 * parseCode("900.956.704")
 * // Returns: { fullCode: "900.956.704", rootCode: "900", itemCode: "956.704", parentCode: "900.956", level: 2, isValid: true }
 */
export function parseCode(fullCode: string): DeweyCodeInfo {
    const sanitized = sanitizeCode(fullCode);
    const parts = sanitized.split('.').filter(Boolean);

    if (parts.length === 0) {
        return {
            fullCode: sanitized,
            rootCode: '',
            itemCode: '',
            parentCode: null,
            level: 0,
            isValid: false,
        };
    }

    return {
        fullCode: sanitized,
        rootCode: parts[0],
        itemCode: parts.length > 1 ? parts.slice(1).join('.') : parts[0],
        parentCode: parts.length > 1 ? parts.slice(0, -1).join('.') : null,
        level: parts.length - 1,
        isValid: parts.every(p => /^\d+$/.test(p)),
    };
}

/**
 * Extracts the actual Dewey decimal code from a hierarchical code.
 * @example extractItemCode("900.956.704") // "956.704"
 */
export function extractItemCode(fullCode: string): string {
    return parseCode(fullCode).itemCode;
}

/**
 * Extracts the parent code from a full hierarchical code.
 * @example extractParentCode("900.956.704") // "900.956"
 */
export function extractParentCode(fullCode: string): string | null {
    return parseCode(fullCode).parentCode;
}

/**
 * Gets the nesting level of a code in the hierarchy.
 * @example getCodeLevel("900.956.704") // 2
 */
export function getCodeLevel(fullCode: string): number {
    return parseCode(fullCode).level;
}

/**
 * Extracts the root category code (first segment).
 * @example extractRootCode("900.956.704") // "900"
 */
export function extractRootCode(fullCode: string): string {
    return parseCode(fullCode).rootCode;
}

// ============================================================================
// Relationship Functions
// ============================================================================

/**
 * Checks if code1 is a direct parent of code2.
 * @example isDirectParent("300.310", "300.310.311") // true
 */
export function isDirectParent(parentCode: string, childCode: string): boolean {
    const parent = parseCode(parentCode);
    const child = parseCode(childCode);

    if (!parent.isValid || !child.isValid) return false;
    if (!childCode.startsWith(parentCode + '.')) return false;

    return child.level === parent.level + 1;
}

/**
 * Checks if code1 is an ancestor of code2.
 * @example isAncestor("300", "300.310.311") // true
 */
export function isAncestor(ancestorCode: string, descendantCode: string): boolean {
    const ancestor = parseCode(ancestorCode);
    const descendant = parseCode(descendantCode);

    if (!ancestor.isValid || !descendant.isValid) return false;

    return descendantCode.startsWith(ancestorCode + '.') && descendant.level > ancestor.level;
}

/**
 * Gets all ancestor codes for a given code.
 * @example getAncestors("900.956.704") // ["900", "900.956"]
 */
export function getAncestors(fullCode: string): string[] {
    const parts = sanitizeCode(fullCode).split('.').filter(Boolean);
    const ancestors: string[] = [];

    for (let i = 1; i < parts.length; i++) {
        ancestors.push(parts.slice(0, i).join('.'));
    }

    return ancestors;
}

// ============================================================================
// Normalization & Variations
// ============================================================================

/**
 * Generates all possible variations of a Dewey code for fuzzy matching.
 * This handles cases where the API format differs from tree format.
 *
 * @example
 * generateCodeVariations("956.704.01")
 * // ["956.704.01", "956.70401", "956.704", "704.01", "70401"]
 */
export function generateCodeVariations(code: string): string[] {
    const sanitized = sanitizeCode(code);
    if (!sanitized) return [];

    const variations = new Set<string>([sanitized]);
    const parts = sanitized.split('.');

    // Strategy 1: Concatenate decimal parts
    // "956.704.01" → "956.70401"
    if (parts.length >= 3) {
        variations.add(`${parts[0]}.${parts.slice(1).join('')}`);
    }

    // Strategy 2: Partial concatenation for 4+ parts
    // "956.704.636.1" → "956.704.6361"
    if (parts.length >= 4) {
        variations.add(`${parts[0]}.${parts[1]}.${parts.slice(2).join('')}`);
    }

    // Strategy 3: Extract item code (without root)
    // "900.956.704" → "956.704"
    if (parts.length >= 2) {
        const itemCode = parts.slice(1).join('.');
        variations.add(itemCode);
        // Also add concatenated version of item code
        if (parts.length >= 3) {
            variations.add(parts.slice(1).join(''));
        }
    }

    // Strategy 4: Split concatenated codes
    // "70401" → "704.01" (try splitting at common positions)
    const lastPart = parts[parts.length - 1];
    if (lastPart.length > 3) {
        // Try splitting after 3 digits: "70401" → "704.01"
        const split3 = `${lastPart.slice(0, 3)}.${lastPart.slice(3)}`;
        variations.add(parts.length > 1
            ? `${parts.slice(0, -1).join('.')}.${split3}`
            : split3
        );
    }

    return Array.from(variations);
}

/**
 * @deprecated Use generateCodeVariations instead
 */
export function normalizeDeweyCode(code: string): string[] {
    return generateCodeVariations(code);
}

// ============================================================================
// Code Search & Matching
// ============================================================================

type LabelFinderFn = (code: string) => string;

/**
 * Builds a search index from category suggestions for efficient lookup.
 */
export function buildCodeIndex(
    categories: Array<{ code: string; label: string }>
): Map<string, string> {
    const index = new Map<string, string>();

    for (const { code, label } of categories) {
        index.set(code, label);
        // Also index without dots for fuzzy matching
        index.set(code.replace(/\./g, ''), label);
    }

    return index;
}

/**
 * Searches for the best matching code using multiple strategies.
 * Returns the code that successfully finds a label.
 */
export function findBestMatchingCode(
    extractedCode: string,
    rootCode: string,
    findLabelFn: LabelFinderFn
): string {
    const sanitized = sanitizeCode(extractedCode);
    if (!sanitized) return extractedCode;

    // Strategy 1: Try exact match
    const exactResult = findLabelFn(sanitized);
    if (exactResult !== sanitized) {
        return sanitized;
    }

    // Strategy 2: Try all variations
    const variations = generateCodeVariations(sanitized);
    for (const variation of variations) {
        if (variation === sanitized) continue;
        const result = findLabelFn(variation);
        if (result !== variation) {
            return variation;
        }
    }

    // Strategy 3: Try with root prefix if not already included
    if (!sanitized.startsWith(rootCode)) {
        const withRoot = `${rootCode}.${sanitized}`;
        const rootResult = findLabelFn(withRoot);
        if (rootResult !== withRoot) {
            return withRoot;
        }
    }

    // Strategy 4: Generate contextual prefixes based on root
    const prefixes = generateContextualPrefixes(rootCode);
    for (const prefix of prefixes) {
        for (const variation of variations) {
            const testCode = `${prefix}.${variation}`;
            const result = findLabelFn(testCode);
            if (result !== testCode) {
                return testCode;
            }
        }
    }

    return extractedCode;
}

/**
 * Generates likely prefixes based on the root Dewey class.
 * Uses knowledge of Dewey structure rather than hardcoded values.
 */
function generateContextualPrefixes(rootCode: string): string[] {
    const root = parseInt(rootCode, 10);
    if (isNaN(root)) return [];

    const prefixes: string[] = [];
    const baseHundred = Math.floor(root / 100) * 100;

    // Generate divisions within the same hundred
    // e.g., for 900: 900, 910, 920, ... 990
    for (let i = 0; i <= 9; i++) {
        const division = baseHundred + i * 10;
        prefixes.push(division.toString().padStart(3, '0'));

        // Also add common subdivisions within each division
        for (let j = 1; j <= 9; j++) {
            prefixes.push((division + j).toString());
        }
    }

    return prefixes;
}

// ============================================================================
// Advanced Search with Caching
// ============================================================================

interface SearchCache {
    hits: Map<string, CodeSearchResult>;
    misses: Set<string>;
}

/**
 * Creates a cached code searcher for efficient repeated lookups.
 */
export function createCodeSearcher(findLabelFn: LabelFinderFn) {
    const cache: SearchCache = {
        hits: new Map(),
        misses: new Set(),
    };

    return {
        /**
         * Searches for a code with caching.
         */
        search(code: string, rootCode?: string): CodeSearchResult {
            const cacheKey = `${rootCode || ''}:${code}`;

            // Check cache
            if (cache.hits.has(cacheKey)) {
                return cache.hits.get(cacheKey)!;
            }
            if (cache.misses.has(cacheKey)) {
                return { code, label: code, matchType: 'none' };
            }

            // Try exact match
            const exactLabel = findLabelFn(code);
            if (exactLabel !== code) {
                const result: CodeSearchResult = { code, label: exactLabel, matchType: 'exact' };
                cache.hits.set(cacheKey, result);
                return result;
            }

            // Try variations
            const variations = generateCodeVariations(code);
            for (const variation of variations) {
                if (variation === code) continue;
                const label = findLabelFn(variation);
                if (label !== variation) {
                    const result: CodeSearchResult = { code: variation, label, matchType: 'normalized' };
                    cache.hits.set(cacheKey, result);
                    return result;
                }
            }

            // Try with root prefix
            if (rootCode) {
                const bestMatch = findBestMatchingCode(code, rootCode, findLabelFn);
                if (bestMatch !== code) {
                    const label = findLabelFn(bestMatch);
                    if (label !== bestMatch) {
                        const result: CodeSearchResult = { code: bestMatch, label, matchType: 'partial' };
                        cache.hits.set(cacheKey, result);
                        return result;
                    }
                }
            }

            // No match found
            cache.misses.add(cacheKey);
            return { code, label: code, matchType: 'none' };
        },

        /**
         * Clears the search cache.
         */
        clearCache() {
            cache.hits.clear();
            cache.misses.clear();
        },

        /**
         * Gets cache statistics.
         */
        getCacheStats() {
            return {
                hits: cache.hits.size,
                misses: cache.misses.size,
            };
        },
    };
}

// ============================================================================
// Hierarchical Tree Traversal
// ============================================================================

interface CategoryNode {
    name?: string;
    divisions?: Record<string, CategoryNode>;
    [key: string]: unknown;
}

/**
 * Finds a category label by traversing a hierarchical tree structure.
 * Handles codes like "900.950" where the path is root["900"].divisions["950"]
 * Also handles codes like "900.950.956.7" where "956.7" is a key in divisions.
 *
 * @param code - Hierarchical code like "900.950.956.7"
 * @param tree - The category tree JSON structure
 * @returns The category name or the original code if not found
 *
 * @example
 * findLabelInHierarchicalTree("900.950", tree) // "History of Asia"
 * findLabelInHierarchicalTree("600.610", tree) // "Medicine & Health"
 */
export function findLabelInHierarchicalTree(
    code: string,
    tree: Record<string, CategoryNode> | null | undefined,
    lang?: string
): string {
    if (!tree || !code || typeof code !== 'string') return code;

    const sanitized = sanitizeCode(code);
    const parts = sanitized.split('.').filter(Boolean);

    if (parts.length === 0) return code;

    // Strategy 1: Direct lookup - code might exist as a key directly
    const directResult = findDirectKey(code, tree, lang);
    if (directResult) return directResult;

    // Strategy 2: Hierarchical traversal for codes like "900.950"
    const hierarchicalResult = traverseHierarchy(parts, tree, lang);
    if (hierarchicalResult) return hierarchicalResult;

    // Strategy 3: Try variations of the code
    const variations = generateCodeVariations(sanitized);
    for (const variation of variations) {
        if (variation === sanitized) continue;
        const variationParts = variation.split('.').filter(Boolean);
        const result = traverseHierarchy(variationParts, tree, lang);
        if (result) return result;
    }

    return code;
}

/**
 * Traverses the tree hierarchy following the path segments.
 * Handles two patterns:
 * 1. "900.950.956" -> tree["900"] -> divisions["950"] -> divisions["956"]
 * 2. "300.320.9567" -> tree["300"] -> divisions["320"] -> deep search for "320.9567"
 *    (where the actual path is divisions["320.9"] -> ["320.95"] -> ["320.956"] -> ["320.9567"])
 */
function traverseHierarchy(
    parts: string[],
    tree: Record<string, CategoryNode>,
    lang?: string
): string | null {
    if (parts.length === 0) return null;

    // Get the root node
    const rootKey = parts[0];
    let currentNode = tree[rootKey];

    if (!currentNode) {
        // Try finding root with padding (e.g., "100" for "1")
        const paddedRoot = rootKey.padStart(3, '0');
        currentNode = tree[paddedRoot];
    }

    if (!currentNode) return null;

    // If only one part, return the root's name
    if (parts.length === 1) {
        return getNodeName(currentNode, lang);
    }

    // Track the current "base" Dewey code as we traverse (for building compound keys)
    let currentBaseCode = '';

    // Navigate through divisions for remaining parts
    for (let i = 1; i < parts.length; i++) {
        const divisions = currentNode.divisions;
        if (!divisions) return null;

        const segment = parts[i];
        let nextNode: CategoryNode | undefined;
        let foundKey: string | undefined;

        // Strategy A: Direct key lookup
        if (divisions[segment]) {
            nextNode = divisions[segment];
            foundKey = segment;
        }

        // Strategy B: Try building compound key with current base code
        // For "300.320.9567" where at 320's divisions, we need "320.9..." keys
        if (!nextNode && currentBaseCode) {
            // Build progressive compound keys: "320.9", "320.95", "320.956", "320.9567"
            const targetSuffix = segment;
            for (let charIdx = 1; charIdx <= targetSuffix.length; charIdx++) {
                const partialSuffix = targetSuffix.slice(0, charIdx);
                const compoundKey = `${currentBaseCode}.${partialSuffix}`;
                if (divisions[compoundKey]) {
                    // Found a match, but we need to continue deeper if not at final char
                    if (charIdx === targetSuffix.length) {
                        nextNode = divisions[compoundKey];
                        foundKey = compoundKey;
                    } else {
                        // Continue traversing deeper through this path
                        const deepResult = findDeepTarget(
                            divisions[compoundKey],
                            currentBaseCode,
                            targetSuffix,
                            charIdx,
                            lang
                        );
                        if (deepResult) {
                            // We found the final target deep in the tree
                            // Skip remaining parts since we've resolved the full path
                            return deepResult;
                        }
                    }
                }
            }
        }

        // Strategy C: For "956.7" style where "7" is the segment but key is "956.7"
        if (!nextNode && i > 1) {
            const prevActualCode = findActualDeweyCode(parts, i - 1, tree);
            if (prevActualCode) {
                const compoundKey = `${prevActualCode}.${segment}`;
                if (divisions[compoundKey]) {
                    nextNode = divisions[compoundKey];
                    foundKey = compoundKey;
                }
            }
        }

        // Strategy D: Try keys that start with the segment
        if (!nextNode) {
            for (const [key, value] of Object.entries(divisions)) {
                if (key === segment || key.startsWith(`${segment}.`)) {
                    nextNode = value;
                    foundKey = key;
                    break;
                }
            }
        }

        // Strategy E: Deep search within divisions for a node with matching key
        // This handles cases where the target is nested several levels deep
        if (!nextNode) {
            const deepSearchResult = deepSearchForCode(divisions, currentBaseCode, segment, lang);
            if (deepSearchResult) {
                return deepSearchResult;
            }
        }

        // Strategy F: For deeply nested codes, try reconstructing the Dewey code
        if (!nextNode) {
            const reconstructedCode = reconstructDeweyCode(parts, i);
            if (divisions[reconstructedCode]) {
                nextNode = divisions[reconstructedCode];
                foundKey = reconstructedCode;
            }
        }

        if (!nextNode) return null;
        
        currentNode = nextNode;
        currentBaseCode = foundKey || segment;
    }

    return getNodeName(currentNode, lang);
}

/**
 * Finds a deep target by traversing through progressively longer compound keys.
 * For "300.320.9567", starting from divisions["320.9"], continues to find "320.9567"
 */
function findDeepTarget(
    node: CategoryNode,
    baseCode: string,
    targetSuffix: string,
    currentCharIdx: number,
    lang?: string
): string | null {
    if (!node.divisions) {
        // Check if we've reached the target
        if (currentCharIdx === targetSuffix.length) {
            return getNodeName(node, lang);
        }
        return null;
    }

    // Try to find the next level
    for (let charIdx = currentCharIdx + 1; charIdx <= targetSuffix.length; charIdx++) {
        const partialSuffix = targetSuffix.slice(0, charIdx);
        const compoundKey = `${baseCode}.${partialSuffix}`;

        if (node.divisions[compoundKey]) {
            if (charIdx === targetSuffix.length) {
                // Found the final target
                return getNodeName(node.divisions[compoundKey], lang);
            } else {
                // Continue deeper
                const result = findDeepTarget(
                    node.divisions[compoundKey],
                    baseCode,
                    targetSuffix,
                    charIdx,
                    lang
                );
                if (result) return result;
            }
        }
    }

    return null;
}

/**
 * Deep searches through all divisions to find a code that matches the target.
 * Handles cases like finding "320.9567" when given base "320" and segment "9567"
 */
function deepSearchForCode(
    divisions: Record<string, CategoryNode>,
    baseCode: string,
    targetSegment: string,
    lang?: string
): string | null {
    // Build the full target code we're looking for
    const fullTargetCode = baseCode ? `${baseCode}.${targetSegment}` : targetSegment;

    // Search recursively through all divisions
    const searchInDivisions = (
        divs: Record<string, CategoryNode>
    ): string | null => {
        for (const [key, value] of Object.entries(divs)) {
            // Check if this key matches our target
            if (key === fullTargetCode || key.endsWith(`.${targetSegment}`) || key === targetSegment) {
                return getNodeName(value, lang);
            }

            // Check if this key is a prefix of our target (e.g., "320.9" is prefix of "320.9567")
            if (fullTargetCode.startsWith(`${key}.`) || fullTargetCode.startsWith(key)) {
                // Continue searching in this node's divisions
                if (value.divisions) {
                    const found = searchInDivisions(value.divisions);
                    if (found) return found;
                }
            }

            // Also recursively search if this node has divisions
            if (value.divisions) {
                // Direct check for target in nested divisions
                if (value.divisions[fullTargetCode]) {
                    return getNodeName(value.divisions[fullTargetCode], lang);
                }
            }
        }
        return null;
    };

    return searchInDivisions(divisions);
}

/**
 * Finds the actual Dewey code at a given level by checking what key exists
 * in the divisions at that point in traversal.
 */
function findActualDeweyCode(
    parts: string[],
    targetIndex: number,
    tree: Record<string, CategoryNode>
): string | null {
    if (targetIndex < 1) return null;

    const rootKey = parts[0];
    let currentNode = tree[rootKey];
    if (!currentNode) return null;

    for (let i = 1; i <= targetIndex; i++) {
        const divisions = currentNode.divisions;
        if (!divisions) return null;

        const segment = parts[i];
        let foundKey: string | null = null;

        // Find the actual key used in divisions
        for (const key of Object.keys(divisions)) {
            if (key === segment || key.endsWith(`.${segment}`) || key.split('.').pop() === segment) {
                foundKey = key;
                currentNode = divisions[key];
                break;
            }
        }

        if (!foundKey) return null;
        if (i === targetIndex) return foundKey;
    }

    return null;
}

/**
 * Reconstructs a potential Dewey code by combining parts.
 * For parts ["900", "950", "956", "7"], index 3 would try "956.7"
 */
function reconstructDeweyCode(parts: string[], index: number): string {
    if (index < 2) return parts[index];

    // The actual Dewey code often includes digits from earlier parts
    // e.g., parts = ["900", "950", "956", "7"] -> at index 3, code might be "956.7"
    // We look at the previous non-root part and combine with current
    const prevPart = parts[index - 1];
    const currentPart = parts[index];

    return `${prevPart}.${currentPart}`;
}

/**
 * Builds the localized name field key for a language, e.g. "ar" -> "nameAr".
 * Returns null for English (base "name" field) or missing language.
 */
function localizedNameKey(lang?: string): string | null {
    if (!lang) return null;
    const base = lang.split('-')[0].toLowerCase();
    if (!base || base === 'en') return null;
    return `name${base.charAt(0).toUpperCase()}${base.slice(1)}`;
}

/**
 * Gets the name from a category node, preferring the localized field
 * (e.g. nameAr, nameCkb) when a language is given, falling back to name.
 */
export function getNodeName(node: CategoryNode, lang?: string): string | null {
    if (typeof node === 'string') return node;
    if (node && typeof node === 'object') {
        const key = localizedNameKey(lang);
        if (key && typeof node[key] === 'string' && node[key]) {
            return node[key] as string;
        }
        if ('name' in node && typeof node.name === 'string') {
            return node.name;
        }
    }
    return null;
}

/**
 * Finds a key directly in the tree by recursive search.
 */
function findDirectKey(
    code: string,
    tree: Record<string, CategoryNode>,
    lang?: string
): string | null {
    for (const [key, value] of Object.entries(tree)) {
        if (key === code) {
            return getNodeName(value as CategoryNode, lang);
        }

        if (value && typeof value === 'object') {
            const node = value as CategoryNode;
            if (node.divisions) {
                const found = findDirectKey(code, node.divisions, lang);
                if (found) return found;
            }
        }
    }
    return null;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Formats a code for display with proper spacing.
 * @example formatCodeForDisplay("95670401") // "956.70401"
 */
export function formatCodeForDisplay(code: string): string {
    const sanitized = code.replace(/[^\d]/g, '');
    if (sanitized.length <= 3) return sanitized;

    return `${sanitized.slice(0, 3)}.${sanitized.slice(3)}`;
}

/**
 * Compares two Dewey codes for sorting.
 * Handles both numeric and hierarchical comparison.
 */
export function compareCodes(a: string, b: string): number {
    const partsA = sanitizeCode(a).split('.').map(Number);
    const partsB = sanitizeCode(b).split('.').map(Number);

    const maxLen = Math.max(partsA.length, partsB.length);

    for (let i = 0; i < maxLen; i++) {
        const valA = partsA[i] ?? 0;
        const valB = partsB[i] ?? 0;
        if (valA !== valB) return valA - valB;
    }

    return 0;
}